/* eslint no-unused-vars: 0 */
// first so it would come first in css compiled file
import '../sass/main.scss';
import '@zanmato/vue3-treeselect/dist/vue3-treeselect.min.css';

// Config
import { createApp, h } from 'vue';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import scrollLockDirective from './directives/scroll-lock';
import FloatingVue from 'floating-vue';
import Multiselect from 'vue-multiselect';
import VueGtag from 'vue-gtag';
import { Chart, registerables } from 'chart.js';
import moment from 'moment';
import 'chartjs-adapter-moment';
import ChartJsPluginDataLabels from 'chartjs-plugin-datalabels';
import Config from '$root/Config';
import { plugin as FormKitPlugin } from '@formkit/vue';
import AirbnbDatepickerAdapter from '$components/AirbnbDatepickerAdapter/AirbnbDatepickerAdapter.vue';
import '@vuepic/vue-datepicker/dist/main.css';

import Notifications from '@kyvg/vue3-notification';
import * as Sentry from "@sentry/vue";

// import store
import MainStore from '$stores/MainStore';

import formKitConfig from './formkit.config.js';
import eventBus from '$utils/eventBus';

// core
import MainRouter from './router/MainRouter';

// general
import App from './App.vue';
import BLM from '$plugins/blm-plugin';
import GlobalError from '$plugins/globalMessage-plugin';
import GetLocationPlugin from '$plugins/getLocation-plugin';

// directives
import vDropZone from '$directives/dropzone';
import vAutoGrow from '$directives/textarea-autogrow';
import vClickOutside from '$directives/click-outside';
import emblaCarousel from '$directives/embla-carousel';

// filter functions (plain exports, registered as $filters global properties)
import { nl2br } from '$filters/nl2br';
import { date as dateFilter } from '$filters/date';
import { fillDegree } from '$filters/fillDegree';
import { illegalDump } from '$filters/illegalDump';
import { sortAlphabetical } from '$filters/sortAlphabetical';
import { cigarettes } from '$filters/cigarettes';
import { cigarettesShort } from '$filters/cigarettesShort';
import { volume } from '$filters/volume';

//global components
import Icon from '$components/Icon/Icon.vue';
import InfoTooltip from '$components/InfoTooltip/InfoTooltip.vue';

//import formkit custom input components
import FormulateDateSelector from './components/Formulate/FormulateDateSelector/FormulateDateSelector.vue';
import FormulateLabel from './components/Formulate/FormulateLabel/FormulateLabel.vue';
import FormulateOptionSelector from './components/Formulate/FormulateOptionSelector/FormulateOptionSelector.vue';
import FormulateStatusSelector from './components/Formulate/FormulateStatusSelector/FormulateStatusSelector.vue';
import FormulateAsyncSelect from './components/Formulate/FormulateAsyncSelect/FormulateAsyncSelect.vue';
import FormulateAddress from './components/Formulate/FormulateAddress/FormulateAddress.vue';
import FormulateBoolFilter from './components/Formulate/FormulateBoolFilter/FormulateBoolFilter.vue';
import FormulateSimpleSelect from './components/Formulate/FormulateSimpleSelect/FormulateSimpleSelect.vue';
import FormulateBoxSelect from './components/Formulate/FormulateBoxSelect/FormulateBoxSelect.vue';
import FormulateTreeSelect from './components/Formulate/FormulateTreeSelect/FormulateTreeSelect.vue';
import FormulateAvSelect from './components/Formulate/FormulateAvSelect/FormulateAvSelect.vue';
import FormulateAsyncOptionSelector from './components/Formulate/FormulateAsyncOptionSelector/FormulateAsyncOptionSelector.vue';
import FormulateBinSlider from './components/Formulate/FormulateBinSlider/FormulateBinSlider.vue';
import FormulateCheckboxCards from './components/Formulate/FormulateCheckboxCards/FormulateCheckboxCards.vue';
import FormulatePassword from './components/Formulate/FormulatePassword/FormulatePassword.vue';
import FormulateTriCheckbox from './components/Formulate/FormulateTriCheckbox/FormulateTriCheckbox.vue';
import FormulateTagField from './components/Formulate/FormulateTagField/FormulateTagField.vue';

// utils
import { urlPattern } from '$utils/Utils';
import chartBackgroundColorPlugin from '$utils/chartBackgroundColorPlugin';
import { checkEmail } from './utils/validationRules';
import { setNotify } from './utils/notify';
import _debounce from 'lodash/debounce';

if (!urlPattern(Config.urlsToExclude, window.location.toString())) {
	Chart.register(chartBackgroundColorPlugin, ChartJsPluginDataLabels, ...registerables);
	Chart.defaults.color = 'rgb(77, 77, 77)';
	Chart.defaults.font.family = `'Flanders Art Sans', sans-serif`;
	Chart.defaults.plugins.datalabels.display = () => false;
	Chart.defaults.plugins.tooltip.enabled = false;
	Chart.defaults.animation = false;
	Chart.defaults.plugins.tooltip.external = function(context) {
		const tooltipModel = context.tooltip;
		let tooltipEl = document.getElementById('chartjs-tooltip');

		if (!tooltipEl) {
			tooltipEl = document.createElement('div');
			tooltipEl.id = 'chartjs-tooltip';
			tooltipEl.classList.add('chartTooltip');
			document.body.appendChild(tooltipEl);
		}

		// Hide if no tooltip
		if (tooltipModel.opacity === 0) {
			tooltipEl.style.opacity = 0;
			tooltipEl.style.display = 'none';
			return;
		}

		// Set caret Position
		tooltipEl.classList.remove('above', 'below', 'no-transform');
		if (tooltipModel.yAlign) {
			tooltipEl.classList.add(tooltipModel.yAlign);
		} else {
			tooltipEl.classList.add('no-transform');
		}

		// Set text
		const titleLines = tooltipModel.title || [];
		const bodyLines = tooltipModel.body.map(b => b.lines);

		let innerHtml = '<div>';
		titleLines.forEach(t => { innerHtml += `<div>${t}</div>`; });
		bodyLines.forEach(b => { innerHtml += `<div class="tooltip-content">${b}</div>`; });
		innerHtml += '</div>';

		tooltipEl.innerHTML = innerHtml;

		// Position tooltip relative to canvas
		const position = context.chart.canvas.getBoundingClientRect();

		// Display, position, and set styles for font
		tooltipEl.style.opacity = 1;
		tooltipEl.style.display = 'block';
		tooltipEl.style.position = 'absolute';
		tooltipEl.style.left = `${position.left + window.pageXOffset + tooltipModel.caretX}px`;
		tooltipEl.style.top = `${position.top + window.pageYOffset + tooltipModel.caretY - 20}px`; // additional 20px to not overlap chart piece data label
		tooltipEl.style.pointerEvents = 'none';
		if (tooltipEl.offsetWidth >= parseInt(position.left + window.pageXOffset + tooltipModel.caretX)) {
			tooltipEl.classList.remove('chartTooltip--right');
			tooltipEl.classList.add('chartTooltip--left');
		} else {
			tooltipEl.classList.remove('chartTooltip--left');
			tooltipEl.classList.add('chartTooltip--right');
		}
	}

	// Setup defaults; this will draw one fully transparent rectangle for each axis.
	Chart.defaults.plugins.gridBgColor = {
		backgroundColorRepeat: false,
		backgroundColor: 'rgba(127,127,127,0.0)',
	};

	const router = MainRouter;

	// Create app
	const app = createApp(App);

	// Sentry (must be called before mount, after createApp)
	if (import.meta.env.PROD) {
		Sentry.init({
			app,
			dsn: 'https://318f8260837fb585e72209509b8ac50e@o146464.ingest.us.sentry.io/4506671083880448',
			integrations: [
				Sentry.browserTracingIntegration({ router }),
			],
			tracesSampleRate: 0.1,
			tracePropagationTargets: ["localhost", /^https:\/\/mijnmooiestraat\.vlaanderen/],
		});
	}

	// Global event bus (Vue 2 $root.$on / $root.$emit replacement)
	app.config.globalProperties.eventBus = eventBus;

	// Register global filter functions as $filters (replaces Vue 2 filter pipe syntax)
	app.config.globalProperties.$filters = {
		nl2br,
		date: dateFilter,
		fillDegree,
		illegalDump,
		sortAlphabetical,
		cigarettes,
		cigarettesShort,
		volume,
	};

	// Plugins
	app.use(BLM);
	app.use(GetLocationPlugin);
	app.use(GlobalError);
	app.use(router);
	app.use(MainStore);

	// Directives
	app.directive('autogrow', vAutoGrow.directive);
	app.directive('dropzone', vDropZone.directive);
	app.directive('click-outside', vClickOutside.directive);
	app.directive('embla-carousel', emblaCarousel.directive);

	app.directive('scroll-lock', scrollLockDirective);
	app.config.globalProperties.$browserDetect = {
		isChrome: /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor),
		isFirefox: /Firefox/.test(navigator.userAgent),
		isSafari: /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
		isEdge: /Edg/.test(navigator.userAgent),
		isOpera: /OPR/.test(navigator.userAgent),
		isIE: /Trident/.test(navigator.userAgent),
	};
	app.use(Notifications);
	app.use(FloatingVue);

	app.use(
		VueGtag,
		{
			config: { id: Config.gtag.id },
			appName: 'Mijn Mooie Straat',
			pageTrackerScreenviewEnabled: true,
		},
		router,
	);
	app.component('Multiselect', Multiselect);
	app.component('Icon', Icon);
	app.component('InfoTooltip', InfoTooltip);
	app.component('airbnb-style-datepicker', AirbnbDatepickerAdapter);

	app.use(VueGoogleMaps, {
		load: false,
	});

	// Register backward-compatible Gmap* aliases for vue2-google-maps naming convention
	// @fawmi/vue-google-maps registers components as GMap* (capital M), but templates use Gmap* (lowercase m)
	const _gmapComps = app._context.components;
	app.component('GmapMap', _gmapComps['GMapMap']);
	app.component('GmapMarker', _gmapComps['GMapMarker']);
	app.component('GmapInfoWindow', _gmapComps['GMapInfoWindow']);
	app.component('GmapCluster', _gmapComps['GMapCluster']);
	app.component('GmapPolyline', _gmapComps['GMapPolyline']);
	app.component('GmapPolygon', _gmapComps['GMapPolygon']);
	app.component('GmapCircle', _gmapComps['GMapCircle']);
	app.component('GmapRectangle', _gmapComps['GMapRectangle']);
	app.component('GmapAutocomplete', _gmapComps['GMapAutocomplete']);
	app.component('GmapHeatmap', _gmapComps['GMapHeatmap']);

	// FormKit (replaces VueFormulate)
	app.component('CustomFormLabel', FormulateLabel);
	app.use(FormKitPlugin, formKitConfig);

	// window leave listener
	const allowPageLeave = true;
	const pageLeaveMessage = '';
	window.addEventListener('beforeunload', (e) => {
		if (!allowPageLeave) {
			e.preventDefault();
			return pageLeaveMessage;
		}
	});

	app.mount('#app');
	// Wire up standalone notify utility for use in Vuex stores
	setNotify(app.config.globalProperties.$notify);
	window.app = app;
} else {
	document.body.classList.add('page--noVue');
}

